import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Stories from '../Stories/Stories';
import Filter from '../Filter/Filter';
import FilterChoices from '../Filter/FilterChoices';
import ResultsToolbar from '../Filter/ResultsToolbar';
import { UserContext } from '../../hooks/UserContext';
import axios from 'axios';
import { getWeek, getDay, getYear } from 'date-fns';
import { getHearingDate, isWithinLastXDays, isExcluded } from './HomeUtils';
import {
  normalizeTag,
  getUniqueNormalizedTags,
} from '../../utils/tagUtils';
import useLawMaker from '../../hooks/useLawMaker';
import { useHttpErrorHandler } from '../../hooks/useHttpErrorHandler';

const FEATURED_API = `${process.env.REACT_APP_FEATURED_API}`;
const MIN_NUMBER_TILES_VISIBLE = 30;

const PageShell = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 2200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
  background: #ffffff;
`;

const CoverageWrapper = styled.div`
  padding: 1rem 2rem 0;

  @media (max-width: 1440px) {
    padding: 1rem 1.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 1rem 1rem 0;
  }
`;

const CoverageMessage = styled.div`
  padding: 24px;
  gap: 10px;
  background: #ffffff;
  border: 1px solid #cccccc;
  box-shadow: 0px 1px 1px #cccccc;
  border-radius: 9px;
  line-height: 1.5;

  a {
    color: #0a819f;
    font-weight: 600;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
`;

const CoverageGreeting = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  margin-block-end: 0.5rem;
`;

export default function Home() {
  const [tags, setTags] = useState([]);
  const [timeFilter] = useState(null);
  const [searchStories, setSearchStories] = useState([]);
  const [sortBy, setSortBy] = useState('score');
  const [clusterBy, setClusterBy] = useState('committee');
  const [legislators, setLegislators] = useState([]);
  const [chamberFilter, setChamberFilter] = useState('both'); // 'both' | 'assembly' | 'senate'
  const [exclusions, setExclusions] = useState([]);
  const [tipsheetType, setTipsheetType] = useState([
    'INFORMATIONAL',
    'PREHEARING',
    'COMMITTEEVOTE',
    'FLOORVOTE',
    'FLOORDISCUSSION',
    'GOVERNORSVETO',
  ]);
  const [originalStories, setOriginalStories] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [filteredStoriesByBill, setFilteredStoriesByBill] = useState([]);
  const [billFilterActive, setBillFilterActive] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [keywords, setKeywords] = useState('');
  const [coverageActive, setCoverageActive] = useState(false);
  const [coverageMessage, setCoverageMessage] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [geoTags, setGeoTags] = useState([]);
  const [clusterMap, setClusterMap] = useState(new Map());
  const [weekToWeekStr, setWeekToWeekStr] = useState({});
  const [lawmakerOptions, setLawmakerOptions] = useState([]);
  const [pidToName, setPidToName] = useState({});
  const [weekToStoryCount, setWeekToStoryCount] = useState({});

  const searchInputRef = useRef(null);

  const { user } = useContext(UserContext);
  const { httpErrorHandler } = useHttpErrorHandler();

  const tipSheetCount = useMemo(
    () =>
      Object.values(weekToStoryCount).reduce((sum, n) => sum + n, 0),
    [weekToStoryCount]
  );

  const { getLawmakersList } = useLawMaker();

  useEffect(() => {
    getLawmakersList().then((lawmakers) => {
      if (lawmakers) {
        setLawmakerOptions(lawmakers);
        // Create a map of pid to full name for easier access
        const nameMap = {};
        lawmakers.forEach((lawmaker) => {
          nameMap[lawmaker.pid] = `${lawmaker.first} ${lawmaker.last}`;
        });
        setPidToName(nameMap);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getLawmakerList needs to only run once
  }, []);

  const fetchAllStories = useCallback(() => {
    setIsLoading(true);

    const axiosInstance = axios.create({
      baseURL: FEATURED_API,
      headers: {
        Authorization: `JWT ${localStorage.getItem('token')}`,
      },
    });

    axiosInstance
      .get('/')
      .then((res) => {
        setOriginalStories(res.data.files);
        setIsLoading(false);
      })
      .catch((err) => {
        httpErrorHandler(err);
        setIsLoading(false);
      });
  }, [httpErrorHandler]);

  useEffect(() => {
    fetchAllStories();
  }, [fetchAllStories]);

  useEffect(() => {
    const allTopicTags = [];
    const geoTopicTags = [];
    const uniqueNormalizedTags = getUniqueNormalizedTags(originalStories);

    uniqueNormalizedTags.forEach((tag) => {
      if (tag.startsWith('geo_')) {
        geoTopicTags.push(tag);
      } else {
        allTopicTags.push(tag);
      }
    });

    setAllTags(allTopicTags);
    setGeoTags(geoTopicTags);
  }, [originalStories]);

  const filterByTags = (stories, tags) => {
    if (tags.length === 0) return stories;
    return stories.filter((file) =>
      file.tipsheet_tile_json?.tags?.some((t) =>
        tags.includes(normalizeTag(t.tagname))
      )
    );
  };

  const filterByTipsheetType = (stories, tipsheetType) => {
    if (tipsheetType.length === 0) return stories;
    return stories.filter((file) =>
      tipsheetType.includes(
        file.tipsheet_tile_json?.type_display_str
          ?.replace(/[^a-zA-Z0-9]/g, '')
          .toUpperCase()
      )
    );
  };

  const filterByLegislators = (stories, legislators) => {
    if (legislators.length === 0) return stories;
    return stories.filter((file) => {
      try {
        return file.tipsheet_tile_json?.personas?.some(
          (persona) =>
            persona.p_type.includes('Legislator') &&
            legislators.some((lawmaker) => persona.pid === lawmaker.pid)
        );
      } catch (e) {
        console.log(e);
        return true;
      }
    });
  };

  // filter stories according to chamberFilter: only include stories where at least
  // one legislator persona belongs to the chosen chamber. Requires access to the
  // full list of lawmakers so we can look up the chamber for a given pid.
  const filterByChamber = (stories, chamber, lawmakerOptions) => {
    if (!chamber || chamber === 'both') return stories;
    const target = chamber === 'senate' ? 'Senate' : 'Assembly';
    return stories.filter((file) => {
      const personas = file.tipsheet_tile_json?.personas;
      if (!Array.isArray(personas)) return false;
      return personas.some((p) => {
        if (!p.p_type.includes('Legislator')) return false;
        const lm = lawmakerOptions.find((l) => l.pid === p.pid);
        if (!lm) return false;
        // treat 'House' same as 'Assembly'
        const chamberName = lm.chamber === 'House' ? 'Assembly' : lm.chamber;
        return chamberName === target;
      });
    });
  };

  const filterByBillNumbers = (stories, bills, isActive) => {
    // Early return if no active filter or no bills
    if (!isActive || bills.length === 0) return stories;

    // Create a Set of filtered story IDs for faster lookup
    const billStoryIds = new Set(bills.map((story) => story.id));

    // Filter stories that match the IDs in our filtered set
    return stories.filter((story) => billStoryIds.has(story.id));
  };

  const fetchStories = useCallback(() => {
    // Use searchStories if search is active, otherwise use originalStories
    let sourceStories = searchActive ? searchStories : originalStories;
    let userFilteredFiles = [...sourceStories];

    // Apply existing filters
    if (timeFilter > 0) {
      userFilteredFiles = userFilteredFiles.filter((x) =>
        isWithinLastXDays(timeFilter, x['tile_date'])
      );
      if (
        userFilteredFiles.length < MIN_NUMBER_TILES_VISIBLE &&
        !searchActive
      ) {
        // Only apply minimum tiles logic when not in search mode
        userFilteredFiles = sourceStories
          .sort((a, b) => getHearingDate(b) - getHearingDate(a))
          .slice(0, Math.min(MIN_NUMBER_TILES_VISIBLE, sourceStories.length));
      }
    }

    userFilteredFiles = userFilteredFiles.filter(
      (story) => !isExcluded(story, exclusions)
    );
    userFilteredFiles = filterByTags(userFilteredFiles, tags);
    userFilteredFiles = filterByTipsheetType(userFilteredFiles, tipsheetType);
    userFilteredFiles = filterByChamber(userFilteredFiles, chamberFilter, lawmakerOptions);
    userFilteredFiles = filterByLegislators(userFilteredFiles, legislators);
    if (billFilterActive && filteredStoriesByBill.length > 0) {
      userFilteredFiles = filterByBillNumbers(
        userFilteredFiles,
        filteredStoriesByBill,
        billFilterActive
      );
    }

    // Process each story with date/week information
    const finalStoryList = userFilteredFiles.map((story) => {
      let storyDate = getHearingDate(story);
      storyDate.setHours(-24 * getDay(storyDate));
      story['week_str'] = storyDate.toLocaleDateString();
      story['week'] =
        story['id'].slice(-2) === 'T3'
          ? 10000
          : getWeek(storyDate) + (getYear(storyDate) - 2010) * 52;
      return story;
    });

    // Apply sorting
    let sortedStories = finalStoryList;
    if (!searchActive) {
      switch (sortBy) {
        case 'chrono':
          sortedStories = finalStoryList.sort(
            (a, b) => getHearingDate(b) - getHearingDate(a)
          );
          break;
        case 'score':
          sortedStories = finalStoryList.sort(
            (a, b) => b.tipsheet_tile_json?.score - a.tipsheet_tile_json?.score
          );
          break;
        default:
          sortedStories = finalStoryList;
      }
    }

    const weekToStoryCount = {};
    sortedStories.forEach((story) => {
      const week = story.week;
      if (!weekToStoryCount[week]) {
        weekToStoryCount[week] = 0;
      }
      weekToStoryCount[week]++;
    });

    setWeekToStoryCount(weekToStoryCount);

    // Apply clustering if selected
    let clusterKeys = [];
    if (clusterBy === 'topic') {
      clusterKeys = [...allTags, ...geoTags];
    } else if (clusterBy === 'legislator') {
      // Use the filtered legislators if available, otherwise use all lawmakers
      clusterKeys =
        legislators.length > 0
          ? legislators.map((l) => l.pid)
          : lawmakerOptions.map((l) => l.pid);
    }

    const clusterMap = new Map();
    const weekToWeekStr = {};

    if (clusterBy === 'topic') {
      // Group stories by week, then by topic within each week

      sortedStories.forEach((story) => {
        const week = story.week;
        const weekStr = story.week_str;
        weekToWeekStr[week] = weekStr;

        if (!clusterMap.has(week)) {
          clusterMap.set(week, new Map());
        }
        const topicTags =
          story.tipsheet_tile_json?.tags
            ?.map((tag) => normalizeTag(tag.tagname))
            .filter((tag) => clusterKeys.includes(tag)) || [];

        topicTags.forEach((topic) => {
          const topicMap = clusterMap.get(week);
          if (!topicMap.has(topic)) {
            topicMap.set(topic, []);
          }
          topicMap.get(topic).push(story);
        });
      });
    } else if (clusterBy === 'legislator') {
      // Group stories by week, then by legislator pid within each week
      sortedStories.forEach((story) => {
        const week = story.week;
        const weekStr = story.week_str;
        weekToWeekStr[week] = weekStr;

        if (!clusterMap.has(week)) {
          clusterMap.set(week, new Map());
        }

        const personas = story.tipsheet_tile_json?.personas || [];
        personas.forEach((persona) => {
          if (
            persona.p_type &&
            persona.p_type.includes('Legislator') &&
            clusterKeys.includes(persona.pid)
          ) {
            const weekMap = clusterMap.get(week);
            if (!weekMap.has(persona.pid)) {
              weekMap.set(persona.pid, []);
            }
            weekMap.get(persona.pid).push(story);
          }
        });
      });
    } else {
      // Group stories by week, then by selected clusterKey within each week
      sortedStories.forEach((story) => {
        const week = story.week;
        const weekStr = story.week_str;
        weekToWeekStr[week] = weekStr;

        if (!clusterMap.has(week)) {
          clusterMap.set(week, new Map());
        }

        let clusterKey = null;
        switch (clusterBy) {
          case 'committee':
            clusterKey = story.tile_committee || 'Uncategorized';
            break;
          case 'bill':
            clusterKey = story.tile_bill_num || 'Uncategorized';
            break;
          case 'none':
            clusterKey = story.id;
            break;
          default:
            clusterKey = 'Uncategorized';
        }

        if (clusterKey) {
          const weekMap = clusterMap.get(week);
          if (!weekMap.has(clusterKey)) {
            weekMap.set(clusterKey, []);
          }
          weekMap.get(clusterKey).push(story);
        }
      });
    }
    setWeekToWeekStr(weekToWeekStr);
    setClusterMap(clusterMap);
  }, [
    originalStories,
    searchStories,
    searchActive,
    timeFilter,
    tags,
    sortBy,
    legislators,
    chamberFilter,
    exclusions,
    tipsheetType,
    filteredStoriesByBill,
    billFilterActive,
    clusterBy,
    allTags,
    geoTags,
    lawmakerOptions,
  ]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  return (
    <PageShell>
      <Filter
        tags={tags}
        setTags={setTags}
        sortBy={sortBy}
        setSortBy={setSortBy}
        legislators={legislators}
        setLegislators={setLegislators}
        setExclusions={setExclusions}
        tipsheetType={tipsheetType}
        setTipsheetType={setTipsheetType}
        setSearchStories={setSearchStories}
        searchActive={searchActive}
        setSearchActive={setSearchActive}
        originalStories={originalStories}
        setFilteredStoriesByBill={setFilteredStoriesByBill}
        setBillFilterActive={setBillFilterActive}
        selectedBills={selectedBills}
        setSelectedBills={setSelectedBills}
        setKeywords={setKeywords}
        searchInputRef={searchInputRef}
        keywords={keywords}
        clusterBy={clusterBy}
        setClusterBy={setClusterBy}
        setCoverageActive={setCoverageActive}
        coverageActive={coverageActive}
        setCoverageMessage={setCoverageMessage}
        allTags={allTags}
        geoTags={geoTags}
        lawmakerOptions={lawmakerOptions}
        chamberFilter={chamberFilter}
        setChamberFilter={setChamberFilter}
        tipSheetCount={tipSheetCount}
      />
      <MainColumn>
        <FilterChoices
          chamberFilter={chamberFilter}
          setChamberFilter={setChamberFilter}
          tags={tags}
          setTags={setTags}
          legislators={legislators}
          setLegislators={setLegislators}
          selectedBills={selectedBills}
          setSelectedBills={setSelectedBills}
          keywords={keywords}
          setKeywords={setKeywords}
          setBillFilterActive={setBillFilterActive}
          setFilteredStoriesByBill={setFilteredStoriesByBill}
          setSearchActive={setSearchActive}
          setSearchStories={setSearchStories}
          searchInputRef={searchInputRef}
          originalStories={originalStories}
          coverageActive={coverageActive}
          variant="mobile-dashboard"
        />
        <ResultsToolbar
          tipSheetCount={tipSheetCount}
          sortBy={sortBy}
          setSortBy={setSortBy}
          clusterBy={clusterBy}
          setClusterBy={setClusterBy}
          searchActive={searchActive}
        />
        {coverageMessage && coverageActive ? (
          <CoverageWrapper>
            <CoverageMessage>
              <CoverageGreeting>
                Hi, {user?.name || ''} welcome to AI Tip Sheets!
              </CoverageGreeting>
              Pro Tip: My Filters tab shows you Tip Sheets that match your
              coverage topics. Go to <Link to="/profile">your profile</Link> to
              choose your coverage topics. To browse all Tip Sheets, choose the
              All Tip Sheets tab in the sidebar.
            </CoverageMessage>
          </CoverageWrapper>
        ) : null}
        <Stories
          legislators={legislators}
          searchActive={searchActive}
          coverageMessage={coverageMessage}
          coverageActive={coverageActive}
          isLoading={isLoading}
          clusterMap={clusterMap}
          weekToWeekStr={weekToWeekStr}
          pidToName={pidToName}
          clusterBy={clusterBy}
          weekToStoryCount={weekToStoryCount}
        />
      </MainColumn>
    </PageShell>
  );
}
